import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Button,
  Dialog,
  TextField,
  IconButton,
  Grid,
  Divider,
  Snackbar,
  Alert,
  Container,
  CircularProgress,
  useTheme
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import { editUserName, changePassword, uploadUserAvatar, SERVER_URL } from '../services/api';
import { useUser } from '../context/UserContext';
import { HASH } from '../utils/helpers';

const Cuenta = () => {
  const theme = useTheme();
  const { user, updateUser } = useUser();
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [avatar, setAvatar] = useState('');
  const [editData, setEditData] = useState({
    name: '',
    email: ''
  });
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user) {
      setEditData({
        name: user.name || '',
        email: user.email || ''
      });
      setAvatar(user.photo || '');
    }
  }, [user]);

  const handleEditModalOpen = () => {
    setError(null);
    setOpenEditModal(true);
  };

  const handleEditModalClose = () => {
    setError(null);
    if (user) {
      setEditData({
        name: user.name || '',
        email: user.email || ''
      });
    }
    setOpenEditModal(false);
  };

  const handlePasswordModalOpen = () => {
    setError(null);
    setPasswordData({
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setOpenPasswordModal(true);
  };

  const handlePasswordModalClose = () => {
    setError(null);
    setPasswordData({
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setOpenPasswordModal(false);
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files[0];
    
    // Validar tipo de archivo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/x-icon'];
    if (!allowedTypes.includes(file.type)) {
      setNotification({
        open: true,
        message: 'Solo se permiten archivos JPG, PNG, JPEG, GIF, WEBP, SVG e ICO',
        severity: 'error'
      });
      return;
    }

    // Validar tamaño (10MB = 10 * 1024 * 1024 bytes)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setNotification({
        open: true,
        message: 'La imagen no puede superar los 10MB',
        severity: 'error'
      });
      return;
    }

    setUploading(true);
    try {
      const userData = await uploadUserAvatar(file);
      
      if (userData) {
        setAvatar(userData.photo);
        // Actualizar el usuario en el contexto con todos los datos actualizados
        updateUser(userData);
        setNotification({
          open: true,
          message: 'Avatar actualizado correctamente',
          severity: 'success'
        });
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
      setNotification({
        open: true,
        message: 'Error al actualizar el avatar',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleEditDataChange = (event) => {
    const { name, value } = event.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null); // Limpiar error cuando el usuario empieza a escribir
  };

  const handlePasswordDataChange = (event) => {
    const { name, value } = event.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleSaveChanges = async () => {
    try {
      if (!editData.name.trim()) {
        throw new Error('El nombre no puede estar vacío');
      }

      const updatedUser = await editUserName(editData.name);
      
      // Actualizar el contexto con los nuevos datos
      updateUser({
        ...user,
        name: editData.name
      });
      
      handleEditModalClose();
      setNotification({
        open: true,
        message: 'Cambios guardados correctamente',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error al guardar los cambios:', error);
      setError(error.message || 'Error al actualizar el nombre');
    }
  };

  const validatePassword = (pass) => {
    // Verificar longitud mínima
    if (pass.length < 6) {
      throw new Error('La contraseña nueva debe tener al menos 6 caracteres');
    }
    
    // Verificar que contenga letras
    if (!/[a-zA-Z]/.test(pass)) {
      throw new Error('La contraseña nueva debe contener al menos una letra');
    }
    
    // Verificar que contenga números
    if (!/[0-9]/.test(pass)) {
      throw new Error('La contraseña nueva debe contener al menos un número');
    }
    
    return true;
  };

  const handleChangePassword = async () => {
    try {
      if (!passwordData.oldPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
        throw new Error('Todos los campos son obligatorios');
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        throw new Error('Las contraseñas nuevas no coinciden');
      }

      // Validar la nueva contraseña
      validatePassword(passwordData.newPassword);

      const hashedOldPassword = HASH(passwordData.oldPassword);
      const hashedNewPassword = HASH(passwordData.newPassword);

      try {
        await changePassword(hashedOldPassword, hashedNewPassword);
        handlePasswordModalClose();
        setNotification({
          open: true,
          message: 'Contraseña actualizada correctamente',
          severity: 'success'
        });
      } catch (apiError) {
        // Si hay un error de red o el servidor no responde, significa que la contraseña actual es incorrecta
        throw new Error('La contraseña actual es incorrecta');
      }
    } catch (error) {
      console.error('Error al cambiar la contraseña:', error);
      setError(error.message || 'Error al cambiar la contraseña');
    }
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  // Si no hay usuario, mostrar mensaje de carga
  if (!user) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
          Cargando datos del usuario...
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth={false}>
      <Box sx={{ py: 0 }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2.5, sm: 3 },
            mb: { xs: 2.5, sm: 3.5 },
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Typography variant="h3" component="h1" sx={{ color: theme.palette.text.primary, mb: 0.5, fontWeight: 800, letterSpacing: '-0.03em', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
            Mi Cuenta
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '1rem', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
            Gestiona tu perfil, tus datos personales y la seguridad de tu cuenta.
          </Typography>
        </Paper>

        <Paper
          elevation={3}
          sx={{
            borderRadius: 2,
            p: 3
          }}
        >
          <Grid container spacing={3}>
            {/* Perfil Principal */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar
                  src={avatar ? `${SERVER_URL}${avatar}` : ''}
                  onClick={() => document.getElementById('avatar-upload').click()}
                  sx={{
                    width: 120,
                    height: 120,
                    border: `2px solid ${theme.palette.primary.main}`,
                    backgroundColor: theme.palette.background.default,
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      transition: 'transform 0.2s ease-in-out'
                    }
                  }}
                >
                  {!avatar && <AccountBoxIcon sx={{ fontSize: 80 }} />}
                </Avatar>
                <IconButton
                  color="primary"
                  aria-label="upload picture"
                  component="label"
                  sx={{
                    position: 'absolute',
                    bottom: -10,
                    right: -10,
                    backgroundColor: theme.palette.background.default,
                    border: `2px solid ${theme.palette.primary.main}`,
                    padding: '8px',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover
                    }
                  }}
                >
                  <input
                    id="avatar-upload"
                    hidden
                    accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,image/x-icon"
                    type="file"
                    onChange={handleAvatarChange}
                  />
                  {uploading ? (
                    <CircularProgress sx={{ color: theme.palette.primary.main }} />
                  ) : (
                    <PhotoCamera />
                  )}
                </IconButton>
              </Box>
              <Typography variant="h6" sx={{ mt: 2, color: theme.palette.text.primary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                {user?.name}
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                {user?.email}
              </Typography>
            </Grid>

            {/* Información y Acciones */}
            <Grid item xs={12} md={8}>
              <Box sx={{ pl: { md: 3 } }}>
                <Typography variant="h6" sx={{ color: theme.palette.text.secondary, mb: 2, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  Detalles de la Cuenta
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                      Nombre
                    </Typography>
                    <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                      {user?.name}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                      {user?.email}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                      Organización
                    </Typography>
                    <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                      {user?.organizations?.find(org => org.code === user.organization)?.name || '-'}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3, backgroundColor: theme.palette.divider }} />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon sx={{ color: theme.palette.primary.contrastText }}/>}
                    onClick={handleEditModalOpen}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        borderColor: theme.palette.primary.dark,
                      },
                      height: '40px',
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      width: { xs: '100%', sm: 'auto' },
                      flex: { xs: '1 1 auto', sm: '0 0 auto' },
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      '& .MuiButton-startIcon': {
                        marginRight: 1,
                        '& svg': {
                          fontSize: '1.2rem'
                        }
                      }
                    }}
                  >
                    Editar Perfil
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<LockIcon sx={{ color: theme.palette.primary.contrastText }}/>}
                    onClick={handlePasswordModalOpen}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        borderColor: theme.palette.primary.dark,
                      },
                      height: '40px',
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      width: { xs: '100%', sm: 'auto' },
                      flex: { xs: '1 1 auto', sm: '0 0 auto' },
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      '& .MuiButton-startIcon': {
                        marginRight: 1,
                        '& svg': {
                          fontSize: '1.2rem'
                        }
                      }
                    }}
                  >
                    Cambiar Contraseña
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Modales */}
      <Dialog
        open={openEditModal}
        onClose={handleEditModalClose}
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 2,
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: theme.palette.background.paper
            },
            '&::-webkit-scrollbar-thumb': {
              background: theme.palette.divider,
              borderRadius: '4px',
              '&:hover': {
                background: theme.palette.primary.main
              }
            }
          }
        }}
      >
        <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 3, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
          Editar Perfil
        </Typography>
        <Box component="form" noValidate onSubmit={(e) => { e.preventDefault(); handleSaveChanges(); }}>
          <TextField
            fullWidth
            label="Nombre"
            name="name"
            value={editData.name}
            onChange={handleEditDataChange}
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.light,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
              },
              '& .MuiInputBase-input': {
                color: theme.palette.text.primary,
              },
            }}
          />
          <TextField
            fullWidth
            label="Correo Electrónico"
            name="email"
            value={editData.email}
            disabled
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
              },
              '& .MuiInputBase-input': {
                color: theme.palette.text.primary,
              },
            }}
          />

          <Button
            variant="contained"
            startIcon={<LockIcon />}
            onClick={handlePasswordModalOpen}
            sx={{
              mt: 2,
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': { backgroundColor: theme.palette.primary.dark },
              textTransform: 'none',
              fontWeight: 'bold',
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
            }}
          >
            Cambiar Contraseña
          </Button>

          {error && (
            <Typography variant="body2" sx={{ color: theme.palette.error.main, mt: 1, mb: 1 }}>
              {error}
            </Typography>
          )}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              onClick={handleEditModalClose}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': { color: theme.palette.text.primary },
                textTransform: 'none',
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveChanges}
              variant="contained"
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
                textTransform: 'none',
                fontWeight: 'bold',
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
              }}
            >
              Guardar
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={openPasswordModal}
        onClose={handlePasswordModalClose}
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 2,
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: theme.palette.background.paper
            },
            '&::-webkit-scrollbar-thumb': {
              background: theme.palette.divider,
              borderRadius: '4px',
              '&:hover': {
                background: theme.palette.primary.main
              }
            }
          }
        }}
      >
        <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 3, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
          Cambiar Contraseña
        </Typography>
        <Box component="form" noValidate onSubmit={(e) => { e.preventDefault(); handleChangePassword(); }}>
          <TextField
            fullWidth
            type="password"
            label="Contraseña Actual"
            name="oldPassword"
            value={passwordData.oldPassword}
            onChange={handlePasswordDataChange}
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.light,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
              },
              '& .MuiInputBase-input': {
                color: theme.palette.text.primary,
              },
            }}
          />
          <TextField
            fullWidth
            type="password"
            label="Nueva Contraseña"
            name="newPassword"
            value={passwordData.newPassword}
            onChange={handlePasswordDataChange}
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.light,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
              },
              '& .MuiInputBase-input': {
                color: theme.palette.text.primary,
              },
            }}
          />
          <TextField
            fullWidth
            type="password"
            label="Confirmar Nueva Contraseña"
            name="confirmPassword"
            value={passwordData.confirmPassword}
            onChange={handlePasswordDataChange}
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.light,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
              },
              '& .MuiInputBase-input': {
                color: theme.palette.text.primary,
              },
            }}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              onClick={handlePasswordModalClose}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': { color: theme.palette.text.primary },
                textTransform: 'none',
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleChangePassword}
              variant="contained"
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
                textTransform: 'none',
                fontWeight: 'bold',
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
              }}
            >
              Guardar
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Notificación */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{
            width: '100%',
            backgroundColor: notification.severity === 'success' ? theme.palette.success.main : theme.palette.error.main,
            color: notification.severity === 'success'
              ? theme.palette.getContrastText(theme.palette.success.main)
              : theme.palette.getContrastText(theme.palette.error.main),
            '& .MuiAlert-icon': {
              color: notification.severity === 'success'
                ? theme.palette.getContrastText(theme.palette.success.main)
                : theme.palette.getContrastText(theme.palette.error.main)
            }
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

    </Container>
  );
};

export default Cuenta;
